import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  fetchProfile,
  fetchMedicalRecords,
  fetchMedicalRecordDetail,
  fetchPrescriptions,
  createMedicalRecord,
  updateBasicProfile,
} from '../services/profileService';
import { fetchTizhiTrend, syncTizhiFromMedicalRecords } from '../services/tizhiService';
import {
  ProfileResponse,
  MedicalRecordSummary,
  MedicalRecordDetail,
  PrescriptionSummary,
  MedicalRecordCreateRequest,
  BasicProfileUpdatePayload,
  TizhiTrendResponse,
} from '../types';
import { Reveal } from './animations';
import './UserProfile.css';

const RECORDS_PER_PAGE = 4;
const PRESCRIPTIONS_PER_PAGE = 4;

const defaultMedicalRecordForm: MedicalRecordCreateRequest = {
  summary: '',
  symptoms: '',
  diagnosis: '',
  notes: '',
  visit_id: undefined,
  metadata: undefined,
};

type EditFormState = {
  name: string;
  age: string;
  gender: string;
  health_conditions: string;
};

const defaultEditForm: EditFormState = {
  name: '',
  age: '',
  gender: '',
  health_conditions: '',
};

type ConstitutionDistributionItem = {
  label: string;
  score: number;
  percentage: number;
};

type ConstitutionTrendPoint = {
  id: string;
  dateLabel: string;
  dominant: string;
  confidence: number;
};

type ConstitutionAlert = {
  id: string;
  label: string;
  level: 'high' | 'medium';
  message: string;
  occurredAt?: string;
};

const TIZHI_ALERT_THRESHOLD = 0.6;

const TIZHI_RISK_RULES: Record<string, { level: 'high' | 'medium'; message: string }> = {
  痰湿质: {
    level: 'medium',
    message: '检测到痰湿体质倾向，建议加强运动，少食肥甘厚味，配合祛湿调理。',
  },
  湿热质: {
    level: 'medium',
    message: '检测到湿热体质倾向，请注意清淡饮食、早睡早起，及时疏散湿热。',
  },
  血瘀质: {
    level: 'high',
    message: '检测到血瘀体质倾向，如伴随胸闷刺痛等症状，请及时就医排查。',
  },
  气郁质: {
    level: 'medium',
    message: '检测到气郁体质倾向，建议调节情志，适度运动，舒缓压力。',
  },
  特禀质: {
    level: 'high',
    message: '检测到特禀体质倾向，如出现过敏反应请尽早干预和就诊。',
  },
};

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const profileLog = useMemo(() => {
    let startTime = performance.now();
    const namespace = '[UserProfile]';
    return {
      info(message: string, payload?: Record<string, unknown>) {
        const now = performance.now();
        const delta = (now - startTime).toFixed(1);
        if (payload) {
          console.debug(`${namespace} ${message}`, { ...payload, ms: delta });
        } else {
          console.debug(`${namespace} ${message}`, { ms: delta });
        }
      },
      warn(message: string, payload?: Record<string, unknown>) {
        console.warn(`${namespace} ${message}`, payload);
      },
      resetTimer(label: string) {
        startTime = performance.now();
        console.debug(`${namespace} timer reset: ${label}`);
      },
    };
  }, []);

  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);

  const [medicalRecords, setMedicalRecords] = useState<MedicalRecordSummary[]>([]);
  const [recordListError, setRecordListError] = useState<string | null>(null);
  const [recordPage, setRecordPage] = useState(1);

  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<MedicalRecordDetail | null>(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);

  const [prescriptions, setPrescriptions] = useState<PrescriptionSummary[]>([]);
  const [prescriptionError, setPrescriptionError] = useState<string | null>(null);
  const [prescriptionPage, setPrescriptionPage] = useState(1);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>(defaultEditForm);
  const [createForm, setCreateForm] = useState<MedicalRecordCreateRequest>({ ...defaultMedicalRecordForm });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [tizhiTrend, setTizhiTrend] = useState<TizhiTrendResponse | null>(null);
  const [constitutionLoading, setConstitutionLoading] = useState(false);
  const [constitutionError, setConstitutionError] = useState<string | null>(null);
  const [tizhiLastRefreshedAt, setTizhiLastRefreshedAt] = useState<Date | null>(null);

  const refreshTizhiInsights = useCallback(async (options?: { force?: boolean }) => {
    const shouldForce = options?.force ?? false;
    profileLog.info('refreshTizhiInsights:start', { force: shouldForce });
    setConstitutionLoading(true);
    setConstitutionError(null);

    let syncError: string | null = null;
    try {
      const payload = shouldForce ? { force: true } : undefined;
      await syncTizhiFromMedicalRecords(payload);
      profileLog.info('refreshTizhiInsights:syncComplete', { force: shouldForce });
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      syncError = detail && typeof detail === 'string'
        ? detail
        : '体质模型暂不可用，无法同步病历数据。';
      profileLog.warn('refreshTizhiInsights:syncError', { force: shouldForce });
    }

    try {
      const trendResponse = await fetchTizhiTrend({ days: 180 });
      setTizhiTrend(trendResponse);
      setTizhiLastRefreshedAt(new Date());
      profileLog.info('refreshTizhiInsights:trendFetched', { total: trendResponse.total_records });
      if (syncError) {
        setConstitutionError(syncError);
      }
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const fallback = syncError ?? '加载体质趋势失败，请稍后再试。';
      setConstitutionError(detail && typeof detail === 'string' ? detail : fallback);
      setTizhiTrend(null);
      profileLog.warn('refreshTizhiInsights:trendError');
    } finally {
      setConstitutionLoading(false);
      profileLog.info('refreshTizhiInsights:complete', { force: shouldForce });
    }
  }, [profileLog]);

  const loadMedicalRecordsList = useCallback(async () => {
    profileLog.info('loadMedicalRecordsList:start');
    try {
      const records = (await fetchMedicalRecords()) ?? [];
      const normalized = Array.isArray(records) ? records : [];
      setMedicalRecords(normalized);
      setRecordListError(null);
      setRecordPage(1);
      profileLog.info('loadMedicalRecordsList:success', { count: normalized.length });

      if (normalized.length === 0) {
        setSelectedRecordId(null);
        setSelectedRecord(null);
      } else {
        setSelectedRecordId((current) => {
          if (current && normalized.some((item) => item.id === current)) {
            return current;
          }
          return normalized[0].id;
        });
      }

      return normalized;
    } catch (error) {
      console.error('Failed to load medical records list', error);
      profileLog.warn('loadMedicalRecordsList:error');
      setMedicalRecords([]);
      setSelectedRecordId(null);
      setSelectedRecord(null);
      setRecordListError('加载病历列表失败，请稍后再试。');
      return [];
    }
  }, [profileLog]);

  const loadPrescriptionsList = useCallback(async () => {
    profileLog.info('loadPrescriptionsList:start');
    try {
      const history = (await fetchPrescriptions()) ?? [];
      const normalized = Array.isArray(history) ? history : [];
      setPrescriptions(normalized);
      setPrescriptionError(null);
      setPrescriptionPage(1);
      profileLog.info('loadPrescriptionsList:success', { count: normalized.length });
      return normalized;
    } catch (error) {
      console.error('Failed to load prescription history', error);
      profileLog.warn('loadPrescriptionsList:error');
      setPrescriptions([]);
      setPrescriptionError('未能加载历史处方，请稍后重试。');
      return [];
    }
  }, [profileLog]);

  const loadRecordDetail = useCallback(async (recordId: number) => {
    profileLog.info('loadRecordDetail:start', { recordId });
    setRecordLoading(true);
    setRecordError(null);
    try {
      const detail = await fetchMedicalRecordDetail(recordId);
      const normalizedDetail: MedicalRecordDetail = {
        ...detail,
        prescriptions: Array.isArray(detail?.prescriptions) ? detail.prescriptions : [],
      };
      setSelectedRecord(normalizedDetail);
      profileLog.info('loadRecordDetail:success', { recordId, prescriptions: normalizedDetail.prescriptions.length });
    } catch (error) {
      console.error('Failed to load medical record detail', error);
      profileLog.warn('loadRecordDetail:error', { recordId });
      setRecordError('加载病历详情失败，请稍后再试。');
    } finally {
      setRecordLoading(false);
      profileLog.info('loadRecordDetail:complete', { recordId });
    }
  }, [profileLog]);

  const loadAll = useCallback(async () => {
    profileLog.resetTimer('loadAll');
    profileLog.info('loadAll:start', { pathname: location.pathname });
    setProfileLoading(true);
    setProfileError(null);

    try {
      const profileData = await fetchProfile();
      setProfile(profileData);
      profileLog.info('loadAll:profileFetched', {
        userId: profileData.user.id,
        stats: profileData.stats,
      });
    } catch (error) {
      console.error('Failed to load profile information', error);
      profileLog.warn('loadAll:profileError');
      setProfile(null);
      setProfileError('加载个人资料失败，请稍后再试。');
      setProfileLoading(false);
      return;
    }

    await Promise.all([loadMedicalRecordsList(), loadPrescriptionsList()]);
    await refreshTizhiInsights();
    setProfileLoading(false);
    profileLog.info('loadAll:complete');
  }, [loadMedicalRecordsList, loadPrescriptionsList, refreshTizhiInsights, location.pathname, profileLog]);

  useEffect(() => {
    profileLog.info('component:mount', { pathname: location.pathname });
    loadAll();
    return () => {
      profileLog.info('component:unmount', { pathname: location.pathname });
    };
  }, [loadAll, location.pathname, profileLog]);

  useEffect(() => {
    if (profile) {
      setEditForm({
        name: profile.user.name ?? '',
        age: profile.user.age != null ? String(profile.user.age) : '',
        gender: profile.user.gender ?? '',
        health_conditions: profile.user.health_conditions ?? '',
      });
    } else {
      setEditForm(defaultEditForm);
    }
  }, [profile]);

  useEffect(() => {
    if (selectedRecordId != null) {
      loadRecordDetail(selectedRecordId);
    } else {
      setSelectedRecord(null);
    }
  }, [selectedRecordId, loadRecordDetail]);

  const recordPageCount = useMemo(() => Math.max(1, Math.ceil(medicalRecords.length / RECORDS_PER_PAGE)), [medicalRecords.length]);
  const paginatedMedicalRecords = useMemo(() => {
    if (medicalRecords.length === 0) {
      return [] as MedicalRecordSummary[];
    }
    const start = (recordPage - 1) * RECORDS_PER_PAGE;
    return medicalRecords.slice(start, start + RECORDS_PER_PAGE);
  }, [medicalRecords, recordPage]);

  const prescriptionPageCount = useMemo(
    () => Math.max(1, Math.ceil(prescriptions.length / PRESCRIPTIONS_PER_PAGE)),
    [prescriptions.length],
  );
  const paginatedPrescriptions = useMemo(() => {
    if (prescriptions.length === 0) {
      return [] as PrescriptionSummary[];
    }
    const start = (prescriptionPage - 1) * PRESCRIPTIONS_PER_PAGE;
    return prescriptions.slice(start, start + PRESCRIPTIONS_PER_PAGE);
  }, [prescriptions, prescriptionPage]);

  const constitutionInsights = useMemo(() => {
    if (!tizhiTrend) {
      return {
        distribution: [] as ConstitutionDistributionItem[],
        trend: [] as ConstitutionTrendPoint[],
        dominantLabel: null as string | null,
        alerts: [] as ConstitutionAlert[],
        totalAnalyzed: 0,
      };
    }

    const totalRecords = typeof tizhiTrend.total_records === 'number' ? tizhiTrend.total_records : 0;
    const distributionEntries = Object.entries(tizhiTrend.constitution_distribution ?? {}).map(([label, rawCount]) => {
      const score = Number(rawCount) || 0;
      const percentage = totalRecords > 0 ? Math.round((score / totalRecords) * 100) : 0;
      return { label, score, percentage };
    });

    const distribution = distributionEntries
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score);

    const trendSource = Array.isArray(tizhiTrend.trend_data) ? tizhiTrend.trend_data : [];
    const processedTrendAll: ConstitutionTrendPoint[] = trendSource
      .map((point, index) => {
        const label = point?.label ?? '未知体质';
        const confidenceRaw = typeof point?.confidence === 'string' ? Number(point.confidence) : point?.confidence ?? 0;
        const confidence = Number.isFinite(confidenceRaw) ? Math.max(0, Math.min(1, confidenceRaw)) : 0;
        const dateIso = point?.date ?? null;
        return {
          id: `${dateIso ?? 'trend'}-${index}`,
          dateLabel: dateIso ? new Date(dateIso).toLocaleDateString() : '日期未知',
          dominant: label,
          confidence,
        };
      })
      .filter((item) => item.dominant && item.dominant.trim() !== '' || item.confidence > 0);

    const recentTrend = processedTrendAll.length > 0 ? processedTrendAll.slice(-6).reverse() : [];

    const alertMap = new Map<string, ConstitutionAlert>();
    trendSource.forEach((point, index) => {
      const label = point?.label;
      if (!label) {
        return;
      }
      const riskRule = TIZHI_RISK_RULES[label];
      if (!riskRule) {
        return;
      }

      const baseConfidence = typeof point?.confidence === 'string' ? Number(point.confidence) : point?.confidence ?? 0;
      const probabilities = Array.isArray(point?.probabilities) ? point.probabilities : [];
      const matchedProbability = probabilities.find((prob) => prob.label === label);
      const effectiveScore = matchedProbability?.score ?? baseConfidence;

      if (!Number.isFinite(effectiveScore) || effectiveScore < TIZHI_ALERT_THRESHOLD) {
        return;
      }

      const alertId = `${label}-${point?.date ?? index}`;
      if (!alertMap.has(alertId)) {
        alertMap.set(alertId, {
          id: alertId,
          label,
          level: riskRule.level,
          message: riskRule.message,
          occurredAt: point?.date ? new Date(point.date).toLocaleString() : undefined,
        });
      }
    });

    const dominantLabel = (() => {
      const main = tizhiTrend.main_constitution;
      if (main && main !== '未知') {
        return main;
      }
      return distribution.length > 0 ? distribution[0].label : null;
    })();

    return {
      distribution,
      trend: recentTrend,
      dominantLabel,
      alerts: Array.from(alertMap.values()),
      totalAnalyzed: totalRecords,
    };
  }, [tizhiTrend]);

  const displayName = profile?.user?.name || profile?.user?.username || '未命名用户';
  const initials = useMemo(() => {
    const source = displayName?.trim();
    if (!source) {
      return 'TC';
    }
    const sanitized = source.replace(/[^A-Za-z0-9\u4e00-\u9fa5]+/g, '');
    if (sanitized.length === 0) {
      return 'TC';
    }
    return sanitized.slice(0, 2).toUpperCase();
  }, [displayName]);

  const registrationDate = profile?.user?.created_at
    ? new Date(profile.user.created_at).toLocaleDateString()
    : '未知';

  const healthSummary = profile?.user?.health_conditions?.trim()
    ? profile.user.health_conditions
    : '完善健康档案，AI 中医助手将提供更精准的调理建议。';

  const {
    distribution: constitutionDistribution,
    trend: constitutionTrend,
    dominantLabel: dominantConstitution,
    alerts: constitutionAlerts,
    totalAnalyzed: constitutionAnalyzedCount,
  } = constitutionInsights;

  const tizhiLastRefreshedLabel = useMemo(() => {
    if (!tizhiLastRefreshedAt) {
      return null;
    }
    return tizhiLastRefreshedAt.toLocaleString();
  }, [tizhiLastRefreshedAt]);

  const handleRetry = () => {
      profileLog.info('handleRetry');
    loadAll();
  };

  const handleSelectRecord = (recordId: number) => {
    profileLog.info('handleSelectRecord', { recordId });
    setSelectedRecordId(recordId);
  };

  const handleEditProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!profile) {
      profileLog.warn('handleEditProfile:skip', { reason: 'missing profile' });
      return;
    }

    const trimmedName = editForm.name.trim();
    const trimmedGender = editForm.gender.trim();
    const trimmedHealth = editForm.health_conditions.trim();
    const ageValue = editForm.age.trim();

    const parsedAge = ageValue === '' ? profile.user.age ?? 0 : Number(ageValue);
    if (Number.isNaN(parsedAge) || parsedAge < 0) {
      setFormError('请输入有效的年龄');
      return;
    }

    const payload: BasicProfileUpdatePayload = {
      name: trimmedName,
      age: parsedAge,
      gender: trimmedGender,
      health_conditions: trimmedHealth,
    };

    setFormSubmitting(true);
    setFormError(null);

    try {
      profileLog.info('handleEditProfile:submit', { userId: profile.user.id });
      await updateBasicProfile(profile.user.id, payload);
      const refreshedProfile = await fetchProfile();
      setProfile(refreshedProfile);
      setIsEditOpen(false);
      profileLog.info('handleEditProfile:success');
    } catch (error) {
      console.error('Failed to update profile', error);
      setFormError('更新资料失败，请稍后再试。');
      profileLog.warn('handleEditProfile:error');
    } finally {
      setFormSubmitting(false);
      profileLog.info('handleEditProfile:complete');
    }
  };

  const handleCreateRecord = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  setFormError(null);
  profileLog.info('handleCreateRecord:submitAttempt');

    if (!createForm.summary || createForm.summary.trim() === '') {
      setFormError('请填写病历摘要');
      profileLog.warn('handleCreateRecord:validationFailed', { field: 'summary' });
      return;
    }

    setFormSubmitting(true);

    const payload: MedicalRecordCreateRequest = {
      visit_id: createForm.visit_id,
      summary: createForm.summary?.trim() ?? '',
      symptoms: createForm.symptoms?.trim(),
      diagnosis: createForm.diagnosis?.trim(),
      notes: createForm.notes?.trim(),
      metadata: createForm.metadata,
    };

    try {
      const created = await createMedicalRecord(payload);
      const normalizedCreated: MedicalRecordDetail = {
        ...created,
        prescriptions: Array.isArray(created?.prescriptions) ? created.prescriptions : [],
      };
      setIsCreateOpen(false);
      setCreateForm({ ...defaultMedicalRecordForm });
      await loadMedicalRecordsList();
      setSelectedRecordId(normalizedCreated.id);
      setSelectedRecord(normalizedCreated);
      await loadPrescriptionsList();
  await refreshTizhiInsights();
      profileLog.info('handleCreateRecord:success', { recordId: normalizedCreated.id });
    } catch (error) {
      console.error('Failed to create medical record', error);
      setFormError('创建病历失败，请稍后重试。');
      profileLog.warn('handleCreateRecord:error');
    } finally {
      setFormSubmitting(false);
      profileLog.info('handleCreateRecord:complete');
    }
  };

  if (profileLoading) {
    return <div className="profile-loading">正在加载个人中心...</div>;
  }

  if (profileError) {
    return (
      <div className="profile-error">
        <span>{profileError}</span>
        <button type="button" className="profile-primary-button" onClick={handleRetry}>
          重试
        </button>
      </div>
    );
  }

  if (!profile) {
    return <div className="profile-empty">暂无个人资料。</div>;
  }

  const totalMedicalRecords = profile?.stats?.total_medical_records ?? medicalRecords.length;
  const totalPrescriptions = profile?.stats?.total_prescriptions ?? prescriptions.length;

  return (
    <motion.div
      className="profile-page"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="profile-content">
        <Reveal as="section" className="profile-hero-banner" motionProps={{ layout: true }}>
          <div className="profile-hero-text">
            <h3>欢迎回来，{displayName}</h3>
            <p>
              您已记录 {totalMedicalRecords} 条病历、整理 {totalPrescriptions} 份处方，持续关注健康状况有助于 AI
              中医助手提供更贴合的调理建议。
            </p>
          </div>
          <div className="profile-hero-badge" aria-label="健康提示">
            <span>今日健康建议</span>
            <strong>保持情绪平和 · 注意作息</strong>
            <span>最新就诊：{selectedRecord?.updated_at ? new Date(selectedRecord.updated_at).toLocaleString() : '暂无记录'}</span>
          </div>
        </Reveal>

        <div className="profile-layout">
          <Reveal as="aside" className="profile-sidebar" aria-label="健康摘要与快捷操作" motionProps={{ layout: true }}>
            <Reveal as="section" className="profile-card profile-info-card" staggerIndex={0}>
              <div className="profile-info-header">
                <div className="profile-avatar" aria-hidden>
                  {initials}
                </div>
                <div>
                  <h3>{displayName}</h3>
                  <p className="profile-username">@{profile.user.username || 'tcm_guest'}</p>
                </div>
              </div>

              <dl className="profile-meta-list">
                {profile.user.email && (
                  <div>
                    <dt>邮箱</dt>
                    <dd>{profile.user.email}</dd>
                  </div>
                )}
                {profile.user.phone && (
                  <div>
                    <dt>电话</dt>
                    <dd>{profile.user.phone}</dd>
                  </div>
                )}
                <div>
                  <dt>注册时间</dt>
                  <dd>{registrationDate}</dd>
                </div>
              </dl>

              <div className="profile-info-tags" aria-label="健康标签">
                <span>性别：{profile.user.gender || '未填写'}</span>
                <span>年龄：{profile.user.age ?? '未填写'}</span>
                <span>{profile.user.health_conditions ? '健康档案已完善' : '健康档案待完善'}</span>
              </div>

              <p className="profile-info-summary">{healthSummary}</p>

              <div className="profile-info-footer">
                <motion.button
                  type="button"
                  className="profile-primary-button"
                  onClick={() => {
                    setIsEditOpen(true);
                    setFormError(null);
                  }}
                  whileHover={{ translateY: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  编辑资料
                </motion.button>
              </div>
            </Reveal>

            <Reveal as="section" className="profile-card profile-stats-card" staggerIndex={1}>
              <h4>健康概览</h4>
              <div className="profile-stat-items">
                <Reveal as="div" className="profile-stat-item" distance={18}>
                  <span className="value">{totalMedicalRecords}</span>
                  <span className="label">病历记录</span>
                  <p>问诊与复诊的完整记录</p>
                </Reveal>
                <Reveal as="div" className="profile-stat-item" distance={18} staggerIndex={1}>
                  <span className="value">{totalPrescriptions}</span>
                  <span className="label">历史处方</span>
                  <p>AI 中医为您定制的方剂</p>
                </Reveal>
                <Reveal as="div" className="profile-stat-item" distance={18} staggerIndex={2}>
                  <span className="value">{profile.user.health_conditions ? '已完善' : '待补充'}</span>
                  <span className="label">健康档案</span>
                  <p>完善档案帮助个性化调理</p>
                </Reveal>
              </div>
            </Reveal>

            <Reveal as="section" className="profile-card profile-quick-actions" staggerIndex={2}>
              <h4>快捷操作</h4>
              <div className="profile-action-buttons">
                <motion.button
                  type="button"
                  className="profile-secondary-button"
                  onClick={() => {
                    setCreateForm({ ...defaultMedicalRecordForm });
                    setFormError(null);
                    setIsCreateOpen(true);
                  }}
                  whileHover={{ translateY: -2 }}
                  whileTap={{ scale: 0.96 }}
                >
                  新增病历
                </motion.button>
                <motion.button
                  type="button"
                  className="profile-secondary-button"
                  onClick={() => navigate('/app/chat')}
                  whileHover={{ translateY: -2 }}
                  whileTap={{ scale: 0.96 }}
                >
                  前往 AI 问诊
                </motion.button>
              </div>
            </Reveal>
          </Reveal>

          <Reveal as="section" className="profile-main" aria-label="病历与处方详情" motionProps={{ layout: true }}>
            <Reveal as="section" className="profile-card profile-constitution-card" staggerIndex={-1}>
              <div className="profile-card-header">
                <div>
                  <h4>体质跟踪仪表盘</h4>
                  <p>
                    系统已分析 {constitutionAnalyzedCount} 条病历，持续跟踪体质变化以便提前调整养护策略。
                  </p>
                </div>
                <div className="profile-card-header-actions">
                  {tizhiLastRefreshedLabel && (
                    <span className="constitution-updated-at">最近刷新：{tizhiLastRefreshedLabel}</span>
                  )}
                  {dominantConstitution && (
                    <span className="constitution-chip">主要体质倾向：{dominantConstitution}</span>
                  )}
                  <motion.button
                    type="button"
                    className="profile-secondary-button constitution-refresh-button"
                    onClick={() => refreshTizhiInsights({ force: true })}
                    disabled={constitutionLoading}
                    whileHover={{ translateY: -2 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    {constitutionLoading ? '刷新中…' : '刷新体质分析'}
                  </motion.button>
                </div>
              </div>

              {constitutionLoading && (
                <div className="panel-loading subtle">正在刷新体质数据...</div>
              )}
              {constitutionError && !constitutionLoading && (
                <div className="panel-error subtle">{constitutionError}</div>
              )}

              <div className="constitution-distribution">
                {constitutionDistribution.length === 0 ? (
                  <div className="empty-state subtle">暂无足够的体质数据，请继续记录病历。</div>
                ) : (
                  constitutionDistribution.map((item) => {
                    const normalized = item.percentage === 0 && item.score > 0 ? 6 : item.percentage;
                    return (
                      <div className="constitution-bar" key={item.label}>
                        <div className="constitution-bar-info">
                          <span>{item.label}</span>
                          <span>{item.percentage}%</span>
                        </div>
                        <div className="constitution-bar-track" aria-hidden>
                          <div
                            className="constitution-bar-fill"
                            style={{ width: `${Math.min(100, normalized)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="constitution-trend">
                <h5>最近体质走势</h5>
                {constitutionTrend.length === 0 ? (
                  <div className="empty-state subtle">暂无趋势数据，请持续积累健康记录。</div>
                ) : (
                  <ol className="constitution-trend-list">
                    {constitutionTrend.map((point) => (
                      <li className="constitution-trend-item" key={`${point.id}-${point.dominant}`}>
                        <span className="constitution-trend-date">{point.dateLabel}</span>
                        <span className="constitution-trend-label">{point.dominant}</span>
                        <span className="constitution-trend-score">置信度 {Math.round(Math.max(0, Math.min(1, point.confidence)) * 100)}%</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {constitutionAlerts.length > 0 && (
                <div className="constitution-alerts">
                  <strong>风险预警</strong>
                  <ul>
                    {constitutionAlerts.map((alert) => (
                      <li key={alert.id}>
                        <span className={`alert-badge alert-${alert.level}`}>
                          {alert.level === 'high' ? '高' : '中'}
                        </span>
                        <span>
                          {alert.label ? `${alert.label}倾向：` : ''}
                          {alert.message}
                        </span>
                        {alert.occurredAt && (
                          <span className="alert-meta">（{alert.occurredAt}）</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Reveal>

            <div className="profile-main-grid">
              <Reveal as="section" className="profile-card profile-records-card" staggerIndex={0}>
                <div className="profile-card-header">
                  <div>
                    <h4>我的病历</h4>
                    <p>跟踪症状变化，辅助医师辨证。</p>
                  </div>
                  <motion.button
                    type="button"
                    className="profile-primary-button"
                    onClick={() => {
                      setCreateForm({ ...defaultMedicalRecordForm });
                      setFormError(null);
                      setIsCreateOpen(true);
                    }}
                    whileHover={{ translateY: -2 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    新增病历
                  </motion.button>
                </div>

                {recordListError && <div className="panel-error">{recordListError}</div>}

                <motion.div className="record-list" layout>
                  {medicalRecords.length === 0 ? (
                    <div className="empty-state">暂无病历记录，可通过问诊或手动新增。</div>
                  ) : (
                    <>
                      {paginatedMedicalRecords.map((record) => {
                        const isActive = selectedRecordId === record.id;
                        return (
                          <Reveal
                            as="button"
                            key={record.id}
                            type="button"
                            className={`record-card ${isActive ? 'active' : ''}`}
                            onClick={() => handleSelectRecord(record.id)}
                            distance={18}
                            motionProps={{
                              whileHover: { translateY: -6 },
                              whileTap: { scale: 0.98 },
                              layout: true,
                            }}
                          >
                            <div className="record-card-header">
                              <h5>{record.summary || '未填写摘要'}</h5>
                              <span className="record-date">
                                {record.updated_at
                                  ? new Date(record.updated_at).toLocaleString()
                                  : record.created_at
                                  ? new Date(record.created_at).toLocaleString()
                                  : '无时间'}
                              </span>
                            </div>
                            <p className="record-diagnosis">辨证：{record.diagnosis || '未填写'}</p>
                          </Reveal>
                        );
                      })}
                      {medicalRecords.length > RECORDS_PER_PAGE && (
                        <div className="profile-pagination">
                          <button
                            type="button"
                            className="profile-secondary-button"
                            onClick={() => setRecordPage((prev) => Math.max(prev - 1, 1))}
                            disabled={recordPage === 1}
                          >
                            上一页
                          </button>
                          <span className="profile-pagination-info">
                            第 {recordPage} / {Math.max(recordPageCount, 1)} 页
                          </span>
                          <button
                            type="button"
                            className="profile-secondary-button"
                            onClick={() => setRecordPage((prev) => Math.min(prev + 1, recordPageCount))}
                            disabled={recordPage === recordPageCount || medicalRecords.length === 0}
                          >
                            下一页
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </motion.div>
              </Reveal>

              <Reveal as="section" className="profile-card profile-detail-card" staggerIndex={1}>
                <div className="profile-card-header compact">
                  <h4>病历详情</h4>
                </div>
                <div className="profile-card-body">
                  <AnimatePresence mode="wait">
                    {recordLoading ? (
                      <motion.div
                        key="record-loading"
                        className="panel-loading"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      >
                        正在加载病历详情...
                      </motion.div>
                    ) : recordError ? (
                      <motion.div
                        key="record-error"
                        className="panel-error"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      >
                        {recordError}
                      </motion.div>
                    ) : !selectedRecord ? (
                      <motion.div
                        key="record-empty"
                        className="empty-state"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      >
                        请选择一条病历查看详细信息。
                      </motion.div>
                    ) : (
                      <motion.div
                        key={`record-${selectedRecord.id}`}
                        className="record-brief"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <Reveal as="div" className="record-detail-row" distance={12}>
                          <span className="label">病历摘要</span>
                          <span className="value strong">{selectedRecord.summary || '未填写'}</span>
                        </Reveal>
                        <Reveal as="div" className="record-detail-row" distance={12} staggerIndex={1}>
                          <span className="label">主诉与症状</span>
                          <span className="value multiline">{selectedRecord.symptoms || '未填写'}</span>
                        </Reveal>
                        <Reveal as="div" className="record-detail-row" distance={12} staggerIndex={2}>
                          <span className="label">辨证结论</span>
                          <span className="value diagnosis">{selectedRecord.diagnosis || '未填写'}</span>
                        </Reveal>
                        <Reveal as="div" className="record-detail-row" distance={12} staggerIndex={3}>
                          <span className="label">就诊/更新</span>
                          <span className="value">
                            {selectedRecord.updated_at
                              ? new Date(selectedRecord.updated_at).toLocaleString()
                              : selectedRecord.created_at
                              ? new Date(selectedRecord.created_at).toLocaleString()
                              : '未知'}
                          </span>
                        </Reveal>
                        <Reveal as="div" className="record-detail-row" distance={12} staggerIndex={4}>
                          <span className="label">关联处方</span>
                          <span className="value">{selectedRecord.prescriptions.length} 条</span>
                        </Reveal>
                        <div className="record-detail-actions">
                          <motion.button
                            type="button"
                            className="profile-primary-button"
                            onClick={() => navigate(`/app/medical-records/${selectedRecord.id}`)}
                            whileHover={{ translateY: -2 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            查看病历详情
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            </div>

            <Reveal as="section" className="profile-card profile-history-card" staggerIndex={2}>
              <div className="profile-card-header">
                <div>
                  <h4>历史处方</h4>
                  <p>回顾 AI 中医给出的调理建议，可一键跳转复诊。</p>
                </div>
              </div>
              <div className="profile-card-body">
                {prescriptionError && <div className="panel-error">{prescriptionError}</div>}
                {prescriptions.length === 0 ? (
                  <div className="empty-state">暂未生成任何处方。</div>
                ) : (
                  <>
                    <div className="prescriptions-grid">
                      {paginatedPrescriptions.map((item) => (
                        <Reveal
                          as="div"
                          className="prescription-card"
                          key={item.id}
                          distance={18}
                          motionProps={{ whileHover: { translateY: -6 } }}
                        >
                          <div className="prescription-card-header">
                            <h5>{item.title || `处方 ${item.prescription_id}`}</h5>
                            <span className="prescription-date">
                              {item.created_at ? new Date(item.created_at).toLocaleString() : '未知时间'}
                            </span>
                          </div>
                          <p className="prescription-diagnosis">辨证：{item.diagnosis_summary || '无'}</p>
                          <p className="prescription-symptoms">症状：{item.symptoms_summary || '无'}</p>
                          <div className="prescription-actions">
                            <Link className="profile-secondary-button" to={`/app/prescriptions/${item.prescription_id}`}>
                              查看详情
                            </Link>
                          </div>
                        </Reveal>
                      ))}
                    </div>
                    {prescriptions.length > PRESCRIPTIONS_PER_PAGE && (
                      <div className="profile-pagination">
                        <button
                          type="button"
                          className="profile-secondary-button"
                          onClick={() => setPrescriptionPage((prev) => Math.max(prev - 1, 1))}
                          disabled={prescriptionPage === 1}
                        >
                          上一页
                        </button>
                        <span className="profile-pagination-info">
                          第 {prescriptionPage} / {Math.max(prescriptionPageCount, 1)} 页
                        </span>
                        <button
                          type="button"
                          className="profile-secondary-button"
                          onClick={() => setPrescriptionPage((prev) => Math.min(prev + 1, prescriptionPageCount))}
                          disabled={prescriptionPage === prescriptionPageCount || prescriptions.length === 0}
                        >
                          下一页
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Reveal>
          </Reveal>
        </div>
      </div>

      {isEditOpen && (
        <div className="profile-modal" role="dialog" aria-modal="true" aria-labelledby="edit-profile-title">
          <div className="profile-modal-content">
            <div className="profile-modal-header">
              <h3 id="edit-profile-title">编辑个人资料</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setIsEditOpen(false);
                  setFormError(null);
                }}
                aria-label="关闭"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditProfile} className="profile-modal-body">
              <label className="modal-field">
                <span>姓名</span>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="请输入姓名"
                />
              </label>
              <label className="modal-field">
                <span>年龄</span>
                <input
                  type="number"
                  min={0}
                  value={editForm.age}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, age: event.target.value }))}
                  placeholder="请输入年龄"
                />
              </label>
              <label className="modal-field">
                <span>性别</span>
                <select
                  value={editForm.gender}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, gender: event.target.value }))}
                >
                  <option value="">请选择</option>
                  <option value="男">男</option>
                  <option value="女">女</option>
                  <option value="其他">其他</option>
                </select>
              </label>
              <label className="modal-field">
                <span>健康状况 / 既往史</span>
                <textarea
                  rows={3}
                  value={editForm.health_conditions}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, health_conditions: event.target.value }))
                  }
                  placeholder="例如：慢性胃炎、易疲劳、过敏史等"
                />
              </label>
              {formError && <div className="modal-error">{formError}</div>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="profile-secondary-button"
                  onClick={() => {
                    setIsEditOpen(false);
                    setFormError(null);
                  }}
                >
                  取消
                </button>
                <button type="submit" className="profile-primary-button" disabled={formSubmitting}>
                  {formSubmitting ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCreateOpen && (
        <div className="profile-modal" role="dialog" aria-modal="true" aria-labelledby="create-record-title">
          <div className="profile-modal-content">
            <div className="profile-modal-header">
              <h3 id="create-record-title">新增病历</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setIsCreateOpen(false);
                  setFormError(null);
                }}
                aria-label="关闭"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateRecord} className="profile-modal-body">
              <label className="modal-field">
                <span>病历摘要</span>
                <input
                  type="text"
                  value={createForm.summary || ''}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, summary: event.target.value }))
                  }
                  placeholder="例如：反复胃脘隐痛三月"
                />
              </label>
              <label className="modal-field">
                <span>症状与体征</span>
                <textarea
                  rows={3}
                  value={createForm.symptoms || ''}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, symptoms: event.target.value }))
                  }
                  placeholder="请概述主诉、伴随症状、舌脉等"
                />
              </label>
              <label className="modal-field">
                <span>辨证分析 / 结论</span>
                <textarea
                  rows={3}
                  value={createForm.diagnosis || ''}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, diagnosis: event.target.value }))
                  }
                  placeholder="例如：脾胃虚寒，寒邪内客"
                />
              </label>
              <label className="modal-field">
                <span>医嘱与调理建议</span>
                <textarea
                  rows={3}
                  value={createForm.notes || ''}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, notes: event.target.value }))}
                  placeholder="饮食调理、情志调摄等建议"
                />
              </label>
              {formError && <div className="modal-error">{formError}</div>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="profile-secondary-button"
                  onClick={() => {
                    setIsCreateOpen(false);
                    setFormError(null);
                  }}
                >
                  取消
                </button>
                <button type="submit" className="profile-primary-button" disabled={formSubmitting}>
                  {formSubmitting ? '保存中...' : '保存病历'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default UserProfile;
